import { AccordionFilter, FacetFilters } from '@/features/shared';
import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import {
  getDataLayerNumFilters,
  getDataLayerSearchFilters,
  getDataLayerSearchFiltersObject,
} from '@/features/shared/HOCs/withGTM';
import { useIsRevision } from '@/features/shared/Hooks';
import { MuiTypography } from '@/features/shared/material-ui';
import { useIsAuthenticated } from '@/hooks';
import { LoadingStatusValues, Theme } from '@/utils/constants';
import { parseBooleanStringToBoolean } from '@/utils/helpers';
import { get, pipe } from 'lodash/fp';
import React, { useContext } from 'react';
import TagManager from 'react-gtm-module';
import PartnerOnlySwitchFilter from '../PartnerOnlySwitchFilter/PartnerOnlySwitchFilter';
import { parsePartnerOnlyFilter } from '../SearchResult.utils';
import useStyles from './Sidebar.styles';

const Sidebar = () => {
  const { isAuthenticated } = useIsAuthenticated();
  const classes = useStyles();
  const isRevision = useIsRevision();
  const [dataLayerId, setDataLayerId] = React.useState('');
  const { labels, queryParams, status, pagination, facets } = useContext(ElasticComponentContext);
  const isPartnerOnlyChecked = React.useMemo(() => {
    const getIsPartnerOnly = pipe(
      parsePartnerOnlyFilter,
      get('is_partner_only'),
      parseBooleanStringToBoolean,
    );

    return getIsPartnerOnly(queryParams?.filters || {});
  }, [queryParams?.filters]);

  React.useEffect(() => {
    if (status !== LoadingStatusValues.loading) return;
    if (dataLayerId !== 'apply_filters') return;

    const filters = queryParams?.filters;
    const searchTerm = queryParams?.q?.trim();
    const searchFiltersObject = getDataLayerSearchFiltersObject(filters);
    const searchFilters = getDataLayerSearchFilters(filters);

    const dataLayer = {
      event: 'trackEvent-filterSearchResult',
      event_name: 'filter_search_results',
      ...(!!searchTerm && { search_term: searchTerm }),
      num_search_result: pagination?.totalResults || 0,
      index_num: String(pagination?.currentPage || 1),
      is_revision: isRevision,
      ...(!!searchFilters && { search_filters: searchFilters }),
      num_search_filters: getDataLayerNumFilters(filters),
      ...searchFiltersObject,
    };
    TagManager.dataLayer({ dataLayer });

    setDataLayerId('');
  }, [
    dataLayerId,
    isRevision,
    pagination?.currentPage,
    pagination?.totalResults,
    queryParams?.filters,
    queryParams?.q,
    status,
  ]);

  return (
    <div className={classes.root}>
      <div className={classes.title}>
        <MuiTypography variant="overline">{labels?.filter_by || 'Filter By'}</MuiTypography>{' '}
        <div>&nbsp;</div>
      </div>
      {isAuthenticated && (
        <div className={classes.partnerOnlyFilter}>
          <PartnerOnlySwitchFilter />
        </div>
      )}
      {facets.map((item) => (
        <AccordionFilter
          title={item.label}
          key={item.name}
          theme={Theme.light}
          expanded={
            isPartnerOnlyChecked
              ? /tax_(language|content_category)/.test(item.name)
              : /tax_content_category/.test(item.name)
          }
        >
          <div className={classes.filters}>
            <FacetFilters
              theme={Theme.light}
              facet={item.name}
              options={item.options}
              labels={labels}
              hasScroll={false}
              CRMAuthorized
              onChange={() => {
                setDataLayerId('apply_filters');
              }}
            />
          </div>
        </AccordionFilter>
      ))}
    </div>
  );
};

export default Sidebar;
